import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import {
  FiSend,
  FiUsers,
  FiShoppingBag,
  FiGlobe,
  FiAlertCircle,
} from "react-icons/fi";
import { usersAPI } from "../../services/api";

const Container = styled.div`
  padding: 2rem;
`;

const Content = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;

  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 1.125rem;
  }
`;

const FormCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
  }

  input,
  textarea,
  select {
    width: 100%;
    padding: 0.75rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.3s;

    &:focus {
      outline: none;
      border-color: #009e49;
    }
  }

  textarea {
    min-height: 120px;
    resize: vertical;
    font-family: inherit;
  }

  .char-count {
    text-align: right;
    color: #666;
    font-size: 0.875rem;
    margin-top: 0.25rem;
  }
`;

const RecipientOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 0.5rem;
`;

const RecipientOption = styled(motion.div)`
  border: 2px solid ${(props) => (props.selected ? "#009e49" : "#e0e0e0")};
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  background: ${(props) => (props.selected ? "#f0f9f4" : "white")};
  transition: all 0.3s;

  &:hover {
    border-color: #009e49;
    background: #f0f9f4;
  }

  .icon {
    font-size: 1.5rem;
    color: ${(props) => (props.selected ? "#009e49" : "#666")};
    margin-bottom: 0.5rem;
  }

  .label {
    font-weight: 600;
    color: #333;
    margin-bottom: 0.25rem;
  }

  .description {
    font-size: 0.875rem;
    color: #666;
  }
`;

const Button = styled(motion.button)`
  background: ${(props) =>
    props.variant === "secondary" ? "#6c757d" : "#009e49"};
  color: white;
  border: none;
  padding: 0.875rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s;

  &:hover {
    background: ${(props) =>
      props.variant === "secondary" ? "#5a6268" : "#007a3a"};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 158, 73, 0.3);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Alert = styled.div`
  background: ${(props) =>
    props.type === "error"
      ? "#fee"
      : props.type === "success"
        ? "#efe"
        : "#fff3cd"};
  border: 1px solid
    ${(props) =>
      props.type === "error"
        ? "#fcc"
        : props.type === "success"
          ? "#cfc"
          : "#ffc107"};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${(props) =>
    props.type === "error"
      ? "#721c24"
      : props.type === "success"
        ? "#155724"
        : "#856404"};
`;

const NotificationManagement = () => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [recipients, setRecipients] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !message.trim()) {
      toast.error("Please fill in both title and message");
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      const response = await usersAPI.sendMassNotification({
        title: title.trim(),
        message: message.trim(),
        recipients,
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setResult({
          type: "success",
          message: response.data.message,
          stats: response.data.stats,
        });
        // Clear form
        setTitle("");
        setMessage("");
        setRecipients("all");
      } else {
        throw new Error(response.data.message || "Failed to send notification");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to send notification";
      toast.error(errorMessage);
      setResult({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const recipientOptions = [
    {
      value: "all",
      label: "All Users",
      description: "Customers and Vendors",
      icon: <FiGlobe />,
    },
    {
      value: "customers",
      label: "Customers Only",
      description: "All registered customers",
      icon: <FiUsers />,
    },
    {
      value: "vendors",
      label: "Vendors Only",
      description: "All registered vendors",
      icon: <FiShoppingBag />,
    },
  ];

  return (
    <Container>
      <Content>
        <PageHeader>
          <h1>Send Mass Notification</h1>
          <p>
            Send custom notifications to users via push notifications and in-app
            messages
          </p>
        </PageHeader>

        <FormCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <form onSubmit={handleSubmit}>
            {result && (
              <Alert type={result.type}>
                <FiAlertCircle />
                <div>
                  <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
                    {result.message}
                  </div>
                   {result.stats && (
                     <div style={{ fontSize: "0.875rem", marginTop: "0.5rem" }}>
                       <div>Total Users: {result.stats.totalUsers}</div>
                       {result.stats.pushNotificationsSent > 0 && (
                         <div style={{ color: "#155724" }}>
                           ✓ Push Notifications Sent:{" "}
                           {result.stats.pushNotificationsSent}
                         </div>
                       )}
                       {result.stats.usersWithoutApp > 0 && (
                         <div style={{ color: "#856404" }}>
                           ⚠ Users without app: {result.stats.usersWithoutApp}
                           <span style={{ fontSize: "0.8rem", marginLeft: "0.5rem" }}>
                             (Notification saved in database)
                           </span>
                         </div>
                       )}
                       {result.stats.pushNotificationsFailed > 0 && (
                         <div style={{ color: "#721c24" }}>
                           ✗ Failed: {result.stats.pushNotificationsFailed}
                         </div>
                       )}
                     </div>
                   )}
                </div>
              </Alert>
            )}

            <FormGroup>
              <label htmlFor="recipients">Recipients *</label>
              <RecipientOptions>
                {recipientOptions.map((option) => (
                  <RecipientOption
                    key={option.value}
                    selected={recipients === option.value}
                    onClick={() => setRecipients(option.value)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="icon">{option.icon}</div>
                    <div className="label">{option.label}</div>
                    <div className="description">{option.description}</div>
                  </RecipientOption>
                ))}
              </RecipientOptions>
            </FormGroup>

            <FormGroup>
              <label htmlFor="title">Notification Title *</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Important Update, New Feature Available"
                maxLength={100}
                required
              />
              <div className="char-count">{title.length}/100</div>
            </FormGroup>

            <FormGroup>
              <label htmlFor="message">Notification Message *</label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your notification message here..."
                maxLength={500}
                required
              />
              <div className="char-count">{message.length}/500</div>
            </FormGroup>

            <ButtonGroup>
              <Button
                type="submit"
                disabled={isLoading || !title.trim() || !message.trim()}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiSend />
                {isLoading ? "Sending..." : "Send Notification"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setTitle("");
                  setMessage("");
                  setRecipients("all");
                  setResult(null);
                }}
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Clear
              </Button>
            </ButtonGroup>
          </form>
        </FormCard>
      </Content>
    </Container>
  );
};

export default NotificationManagement;
